import React, { useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { ShieldCheck } from "lucide-react";

const METRIC_OPTIONS = [
  { key: "exit_velocity", label: "Exit Velocity", units: "mph", lower_better: false },
  { key: "average_exit_velocity", label: "Avg Exit Velocity", units: "mph", lower_better: false },
  { key: "bat_speed", label: "Bat Speed", units: "mph", lower_better: false },
  { key: "ten_yard_sprint", label: "10-Yard Sprint", units: "s", lower_better: true },
  { key: "thirty_yard_sprint", label: "30-Yard Sprint", units: "s", lower_better: true },
  { key: "sixty_yard_sprint", label: "60-Yard Sprint", units: "s", lower_better: true },
  { key: "five_ten_five_shuttle", label: "5-10-5 Shuttle", units: "s", lower_better: true },
  { key: "infield_velocity", label: "Infield Velocity", units: "mph", lower_better: false },
  { key: "outfield_velocity", label: "Outfield Velocity", units: "mph", lower_better: false },
  { key: "catcher_velocity", label: "Catcher Velocity", units: "mph", lower_better: false },
  { key: "pop_time", label: "Pop Time", units: "s", lower_better: true },
  { key: "fastball_velocity", label: "Fastball Velocity", units: "mph", lower_better: false },
  { key: "spin_rate", label: "Spin Rate", units: "rpm", lower_better: false },
];

/**
 * Admin form to add a metric entry for a player.
 * Defaults to cbr_verified status (CBR staff entering at an event).
 */
export default function MetricEntryForm({ playerId, camps = [], defaultCamp = null, onSaved }) {
  const [metricType, setMetricType] = useState("exit_velocity");
  const [value, setValue] = useState("");
  const [campId, setCampId] = useState(defaultCamp?.id || "");
  const [eventDate, setEventDate] = useState(new Date().toISOString().slice(0, 10));
  const [verification, setVerification] = useState("cbr_verified");
  const [verifierName, setVerifierName] = useState("");
  const [notes, setNotes] = useState("");
  const [error, setError] = useState("");
  const [saving, setSaving] = useState(false);

  const selected = METRIC_OPTIONS.find((m) => m.key === metricType) || METRIC_OPTIONS[0];

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (!value || isNaN(Number(value))) {
      setError("Enter a numeric value");
      return;
    }
    setSaving(true);
    try {
      const selectedCamp = camps.find((c) => c.id === campId);
      const payload = {
        metric_type: metricType,
        value: Number(value),
        units: selected.units,
        verification_status: verification,
        event_date: eventDate ? new Date(eventDate).toISOString() : null,
        verified_by_name: verifierName || null,
        notes: notes || null,
      };
      if (selectedCamp) {
        payload.event_id = selectedCamp.id;
        payload.event_name = selectedCamp.title;
      }
      await api.post(`/players/${playerId}/metrics`, payload);
      setValue("");
      setNotes("");
      if (onSaved) await onSaved();
    } catch (err) {
      setError(formatApiError(err?.response?.data?.detail) || err.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <form
      data-testid="metric-entry-form"
      onSubmit={submit}
      className="bg-white border border-slate-200 rounded-sm p-5"
    >
      <div className="flex items-center gap-2">
        <ShieldCheck className="w-5 h-5 text-[#E63946]" />
        <h3 className="font-display text-lg uppercase tracking-tight">Record Metric</h3>
      </div>

      {error && (
        <div className="border-l-4 border-[#E63946] bg-red-50 p-2 text-xs text-[#E63946] mt-3">{error}</div>
      )}

      <div className="grid sm:grid-cols-2 gap-3 mt-4">
        <div>
          <label className="label-overline">Metric</label>
          <select
            data-testid="metric-entry-type"
            value={metricType}
            onChange={(e) => setMetricType(e.target.value)}
            className="mt-1 w-full px-3 py-2 border border-slate-300 rounded-sm bg-white text-sm"
          >
            {METRIC_OPTIONS.map((m) => (
              <option key={m.key} value={m.key}>
                {m.label} ({m.units})
              </option>
            ))}
          </select>
        </div>
        <div>
          <label className="label-overline">Value ({selected.units})</label>
          <input
            data-testid="metric-entry-value"
            type="number"
            step="0.01"
            value={value}
            onChange={(e) => setValue(e.target.value)}
            className="mt-1 w-full px-3 py-2 border border-slate-300 rounded-sm text-sm"
          />
        </div>
        <div>
          <label className="label-overline">Event / Camp</label>
          <select
            value={campId}
            onChange={(e) => setCampId(e.target.value)}
            className="mt-1 w-full px-3 py-2 border border-slate-300 rounded-sm bg-white text-sm"
          >
            <option value="">— No event (independent test) —</option>
            {camps.map((c) => (
              <option key={c.id} value={c.id}>
                {c.title}
              </option>
            ))}
          </select>
        </div>
        <div>
          <label className="label-overline">Event Date</label>
          <input
            type="date"
            value={eventDate}
            onChange={(e) => setEventDate(e.target.value)}
            className="mt-1 w-full px-3 py-2 border border-slate-300 rounded-sm text-sm"
          />
        </div>
        <div>
          <label className="label-overline">Verification</label>
          <select
            data-testid="metric-entry-verification"
            value={verification}
            onChange={(e) => setVerification(e.target.value)}
            className="mt-1 w-full px-3 py-2 border border-slate-300 rounded-sm bg-white text-sm"
          >
            <option value="cbr_verified">CBR Verified</option>
            <option value="staff_entered">Staff Entered</option>
            <option value="self_reported">Self Reported</option>
          </select>
        </div>
        <div>
          <label className="label-overline">Verified By (name)</label>
          <input
            value={verifierName}
            onChange={(e) => setVerifierName(e.target.value)}
            placeholder="e.g. CBR Staff – J. Martinez"
            className="mt-1 w-full px-3 py-2 border border-slate-300 rounded-sm text-sm"
          />
        </div>
        <div className="sm:col-span-2">
          <label className="label-overline">Notes</label>
          <input
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            placeholder="Optional · field conditions, equipment used, etc."
            className="mt-1 w-full px-3 py-2 border border-slate-300 rounded-sm text-sm"
          />
        </div>
      </div>

      <div className="mt-4 flex justify-end">
        <button
          data-testid="metric-entry-submit"
          disabled={saving}
          className="bg-[#E63946] hover:bg-[#D90429] text-white px-6 py-2 text-xs font-semibold uppercase tracking-wider rounded-sm disabled:opacity-60"
        >
          {saving ? "Saving…" : "Add Metric"}
        </button>
      </div>
    </form>
  );
}
